// =============================================================================
// src/chrome/topbar/topbar-popovers.ts — popovers flutuantes da topbar (overflow + conta).
//
// Proveniencia: porte FIEL de assets/unified-app.js TopbarNav._wirePopover/
//   _showMenu/_closeMenu (2893-2936) + os escutadores globais de fechar em
//   click-fora/Esc (3219-3233). Posicionamento e timings sao paridade:
//     · menu abre 6px abaixo do trigger (rect.bottom + 6)
//     · alinhado a direita: right = max(12, innerWidth - rect.right)
//     · foco no 1o item apos 30ms (deixa o display restaurar)
//   NAO alterar nenhum desses numeros.
//
// SRP: este arquivo so abre/fecha/posiciona os popovers. As ACOES dos itens
//   (bookmark/download/workspace/...) ficam no index.ts (TopbarNav), que conhece
//   o AppContext e o MiniToast.
// =============================================================================

import { qs } from '../../app/dom';

/** Distancia vertical do menu abaixo do trigger (legado: rect.bottom + 6). */
const POPOVER_GAP_PX = 6;
/** Margem direita minima da viewport (legado: max(12, ...)). */
const POPOVER_MIN_RIGHT_PX = 12;
/** Largura assumida antes de medir o menu no DOM (legado: 260). */
const POPOVER_FALLBACK_WIDTH_PX = 260;
/** Ms ate focar o 1o item do menu (legado: 30). CONSTRAINT de paridade. */
const POPOVER_FOCUS_DELAY_MS = 30;
/** Seletor do item focavel dentro do popover. */
const POPOVER_ITEM_SELECTOR = '.u-topbar-popover-item';

/**
 * Controla o par overflow/conta como um unico popover-aberto-por-vez. Substitui
 * o estado `_openMenu` do TopbarNav legado, sem global escondido: a instancia e
 * criada pelo TopbarNav e mantida no escopo do modulo.
 */
export class TopbarPopoverController {
  private openMenu: HTMLElement | null = null;

  /**
   * Liga um trigger ao seu menu: clique alterna abrir/fechar; reabrir o mesmo
   * fecha (toggle), abrir outro troca. Replica TopbarNav._wirePopover.
   */
  wire(triggerId: string, menuId: string): void {
    const trigger = qs<HTMLElement>(`#${triggerId}`);
    const menu = qs<HTMLElement>(`#${menuId}`);
    if (!trigger || !menu) return;
    trigger.addEventListener('click', (event) => this.onTriggerClick(event, trigger, menu));
  }

  /** Liga os escutadores globais de fechar (click-fora + Esc). */
  wireGlobalDismiss(): void {
    document.addEventListener('click', (event) => this.onDocumentClick(event));
    document.addEventListener('keydown', (event) => this.onDocumentKeydown(event));
  }

  /** Clique no trigger: toggle se ja aberto, senao fecha o atual e abre este. */
  private onTriggerClick(event: MouseEvent, trigger: HTMLElement, menu: HTMLElement): void {
    event.stopPropagation();
    if (this.openMenu === menu) {
      this.close();
      return;
    }
    this.close();
    this.show(trigger, menu);
  }

  /** Posiciona e exibe o menu abaixo/a-direita do trigger (replica _showMenu). */
  private show(trigger: HTMLElement, menu: HTMLElement): void {
    const rect = trigger.getBoundingClientRect();
    menu.hidden = false;
    const right = Math.max(POPOVER_MIN_RIGHT_PX, window.innerWidth - rect.right);
    menu.style.top = `${rect.bottom + POPOVER_GAP_PX}px`;
    menu.style.right = `${right}px`;
    menu.style.left = 'auto';
    trigger.setAttribute('aria-expanded', 'true');
    this.openMenu = menu;
    this.focusFirstItem(menu);
  }

  /** Foca o 1o item apos 30ms (legado mede width no DOM; a largura nao e usada). */
  private focusFirstItem(menu: HTMLElement): void {
    const first = qs<HTMLElement>(POPOVER_ITEM_SELECTOR, menu);
    if (first) setTimeout(() => first.focus(), POPOVER_FOCUS_DELAY_MS);
  }

  /** Fecha o menu aberto e devolve o aria-expanded do trigger (replica _closeMenu). */
  close(): void {
    if (!this.openMenu) return;
    const menu = this.openMenu;
    menu.hidden = true;
    this.resetTriggerExpanded(menu);
    this.openMenu = null;
  }

  /** Restaura aria-expanded=false no trigger correspondente ao menu fechado. */
  private resetTriggerExpanded(menu: HTMLElement): void {
    const triggerId = menu.id === 'topbarOverflowMenu' ? 'topbarOverflow' : 'topbarAvatar';
    const trigger = qs<HTMLElement>(`#${triggerId}`);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  /** Click-fora fecha (ignora clique no proprio menu ou no seu trigger). */
  private onDocumentClick(event: MouseEvent): void {
    if (!this.openMenu) return;
    const target = event.target as Node;
    if (this.openMenu.contains(target)) return;
    const triggerId = this.openMenu.id === 'topbarOverflowMenu' ? 'topbarOverflow' : 'topbarAvatar';
    const trigger = qs<HTMLElement>(`#${triggerId}`);
    if (trigger && trigger.contains(target)) return;
    this.close();
  }

  /** Esc fecha o popover aberto (replica o keydown global legado). */
  private onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.openMenu) this.close();
  }
}
